/**
 * Chord Generator Utility
 * Algorithmic chord progression generation based on music theory
 * Supports multiple genres, keys, scales, and progression patterns
 */

// Musical constants
export const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export const NOTE_TO_MIDI = { 'C': 0, 'C#': 1, 'D': 2, 'D#': 3, 'E': 4, 'F': 5, 'F#': 6, 'G': 7, 'G#': 8, 'A': 9, 'A#': 10, 'B': 11 };

// Scale intervals (semitones from root)
export const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  melodicMinor: [0, 2, 3, 5, 7, 9, 11],
  pentatonicMajor: [0, 2, 4, 7, 9],
  pentatonicMinor: [0, 3, 5, 7, 10],
  blues: [0, 3, 5, 6, 7, 10],
};

// Chord types with intervals from root
export const CHORD_TYPES = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  diminished: [0, 3, 6],
  augmented: [0, 4, 8],
  major7: [0, 4, 7, 11],
  minor7: [0, 3, 7, 10],
  dominant7: [0, 4, 7, 10],
  diminished7: [0, 3, 6, 9],
  halfDiminished7: [0, 3, 6, 10],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  add9: [0, 4, 7, 14],
  minorAdd9: [0, 3, 7, 14],
  major9: [0, 4, 7, 11, 14],
  minor9: [0, 3, 7, 10, 14],
  power: [0, 7],
};

// Chord quality for each scale degree (major scale)
const MAJOR_SCALE_CHORDS = ['major', 'minor', 'minor', 'major', 'major', 'minor', 'diminished'];
const MINOR_SCALE_CHORDS = ['minor', 'diminished', 'major', 'minor', 'minor', 'major', 'major'];

// Genre-specific progression patterns (Roman numeral positions, 0-indexed)
export const GENRE_PROGRESSIONS = {
  pop: {
    name: 'Pop',
    patterns: [
      [0, 4, 5, 3],      // I-V-vi-IV (most popular)
      [0, 3, 4, 4],      // I-IV-V-V
      [0, 5, 3, 4],      // I-vi-IV-V
      [5, 3, 0, 4],      // vi-IV-I-V
      [0, 4, 1, 3],      // I-V-ii-IV
      [0, 3, 0, 4],      // I-IV-I-V
    ],
    tempo: { min: 100, max: 130 },
    useExtensions: false,
  },
  hiphop: {
    name: 'Hip-Hop',
    patterns: [
      [0, 5, 3, 4],      // i-vi-IV-V (minor)
      [0, 6, 3, 4],      // i-VII-IV-V
      [0, 3, 6, 4],      // i-iv-VII-V
      [0, 4, 3, 6],      // i-v-iv-VII
      [0, 0, 3, 3],      // i-i-iv-iv
      [0, 5, 4, 3],      // i-vi-v-iv
    ],
    tempo: { min: 70, max: 100 },
    useExtensions: true,
    preferMinor: true,
  },
  edm: {
    name: 'EDM/Electronic',
    patterns: [
      [0, 5, 3, 4],      // i-vi-IV-V
      [0, 4, 5, 3],      // I-V-vi-IV
      [5, 3, 0, 4],      // vi-IV-I-V
      [0, 3, 5, 4],      // i-iv-vi-V
      [0, 0, 3, 4],      // i-i-iv-V
      [5, 4, 0, 3],      // vi-V-I-IV
    ],
    tempo: { min: 120, max: 140 },
    useExtensions: false,
  },
  jazz: {
    name: 'Jazz',
    patterns: [
      [1, 4, 0, 0],      // ii-V-I-I
      [0, 5, 1, 4],      // I-vi-ii-V (rhythm changes)
      [0, 0, 3, 4],      // I-I-IV-V
      [1, 4, 0, 5],      // ii-V-I-vi
      [2, 5, 1, 4],      // iii-vi-ii-V
      [0, 6, 5, 1],      // I-VII-vi-ii
    ],
    tempo: { min: 100, max: 180 },
    useExtensions: true,
  },
  rnb: {
    name: 'R&B/Soul',
    patterns: [
      [0, 3, 1, 4],      // I-IV-ii-V
      [0, 5, 1, 4],      // I-vi-ii-V
      [2, 5, 1, 4],      // iii-vi-ii-V
      [0, 6, 5, 3],      // I-VII-vi-IV
      [0, 2, 3, 4],      // I-iii-IV-V
      [5, 1, 4, 0],      // vi-ii-V-I
    ],
    tempo: { min: 60, max: 90 },
    useExtensions: true,
  },
  rock: {
    name: 'Rock',
    patterns: [
      [0, 3, 4, 4],      // I-IV-V-V
      [0, 4, 3, 3],      // I-V-IV-IV
      [0, 6, 3, 4],      // I-bVII-IV-V
      [0, 3, 0, 4],      // I-IV-I-V
      [0, 5, 3, 4],      // I-vi-IV-V
      [0, 0, 3, 4],      // I-I-IV-V (12-bar feel)
    ],
    tempo: { min: 100, max: 140 },
    useExtensions: false,
  },
  lofi: {
    name: 'Lo-Fi',
    patterns: [
      [1, 4, 2, 5],      // ii-V-iii-vi
      [0, 5, 3, 2],      // I-vi-IV-iii
      [5, 1, 4, 0],      // vi-ii-V-I
      [2, 5, 0, 3],      // iii-vi-I-IV
      [0, 2, 5, 3],      // I-iii-vi-IV
      [1, 5, 2, 4],      // ii-vi-iii-V
    ],
    tempo: { min: 70, max: 90 },
    useExtensions: true,
    preferMinor: true,
  },
  cinematic: {
    name: 'Cinematic/Epic',
    patterns: [
      [0, 5, 3, 4],      // I-vi-IV-V
      [0, 3, 5, 4],      // I-IV-vi-V
      [5, 3, 0, 4],      // vi-IV-I-V
      [0, 0, 5, 5],      // I-I-vi-vi
      [3, 0, 4, 5],      // IV-I-V-vi
      [0, 5, 4, 3],      // I-vi-V-IV
    ],
    tempo: { min: 60, max: 100 },
    useExtensions: false,
  },
};

// Chord voicing variations
const VOICING_STYLES = {
  basic: { spread: 0, octave: 4 },
  wide: { spread: 1, octave: 3 },
  high: { spread: 0, octave: 5 },
  power: { spread: 0, octave: 3, usePowerChords: true },
};

/**
 * Get MIDI note number from note name and octave
 */
export const noteToMidi = (note, octave = 4) => {
  const noteIndex = NOTE_TO_MIDI[note];
  if (noteIndex === undefined) return null;
  return noteIndex + (octave + 1) * 12;
};

/**
 * Get note name from MIDI number
 */
export const midiToNote = (midi) => {
  const noteIndex = midi % 12;
  const octave = Math.floor(midi / 12) - 1;
  return { note: NOTES[noteIndex], octave };
};

/**
 * Get scale notes for a given root and scale type
 */
export const getScaleNotes = (root, scaleType = 'major') => {
  const rootIndex = NOTE_TO_MIDI[root];
  const intervals = SCALES[scaleType] || SCALES.major;
  
  return intervals.map(interval => {
    const noteIndex = (rootIndex + interval) % 12;
    return NOTES[noteIndex];
  });
};

/**
 * Build a chord from root note, chord type, and octave
 */
export const buildChord = (root, chordType = 'major', octave = 4, inversion = 0) => {
  const rootMidi = noteToMidi(root, octave);
  const intervals = CHORD_TYPES[chordType] || CHORD_TYPES.major;
  
  let notes = intervals.map(interval => rootMidi + interval);
  
  // Apply inversion
  for (let i = 0; i < inversion && i < notes.length - 1; i++) {
    notes[i] += 12;
  }
  
  // Sort by pitch
  notes.sort((a, b) => a - b);
  
  return notes;
};

/**
 * Get the chord type for a scale degree
 */
const getChordTypeForDegree = (degree, isMinor = false, useExtensions = false) => {
  const baseChords = isMinor ? MINOR_SCALE_CHORDS : MAJOR_SCALE_CHORDS;
  let chordType = baseChords[degree % 7];
  
  if (useExtensions) {
    // Add 7ths to some chords for more color
    if (chordType === 'major') {
      return Math.random() > 0.5 ? 'major7' : 'major';
    } else if (chordType === 'minor') {
      return Math.random() > 0.5 ? 'minor7' : 'minor';
    } else if (chordType === 'diminished') {
      return 'halfDiminished7';
    }
  }
  
  return chordType;
};

/**
 * Generate a chord progression based on parameters
 */
export const generateChordProgression = ({
  key = 'C',
  scale = 'major',
  genre = 'pop',
  length = 4,
  complexity = 'medium',
  useExtensions = null,
}) => {
  const genreConfig = GENRE_PROGRESSIONS[genre] || GENRE_PROGRESSIONS.pop;
  const isMinor = scale === 'minor' || scale === 'harmonicMinor' || scale === 'melodicMinor' || genreConfig.preferMinor;
  const shouldUseExtensions = useExtensions !== null ? useExtensions : genreConfig.useExtensions;
  
  // Select a random progression pattern
  const patterns = genreConfig.patterns;
  const pattern = patterns[Math.floor(Math.random() * patterns.length)];
  
  // Build the scale
  const scaleNotes = getScaleNotes(key, isMinor ? 'minor' : scale);
  
  // Generate chords
  const chords = [];
  
  for (let i = 0; i < length; i++) {
    const patternIndex = i % pattern.length;
    const degree = pattern[patternIndex];
    const root = scaleNotes[degree % scaleNotes.length];
    const chordType = getChordTypeForDegree(degree, isMinor, shouldUseExtensions);
    
    // Determine octave based on complexity
    let octave = 4;
    if (complexity === 'simple') octave = 4;
    else if (complexity === 'medium') octave = 3 + Math.floor(Math.random() * 2);
    else if (complexity === 'complex') octave = 3;
    
    // Build chord notes
    const notes = buildChord(root, chordType, octave, 0);
    
    // Create chord object
    chords.push({
      root,
      type: chordType,
      notes,
      degree: degree + 1, // 1-indexed for display
      symbol: getChordSymbol(root, chordType),
      duration: 1, // in beats (default 1 bar = 4 beats for 4/4)
      startBeat: i * 4, // Each chord is 1 bar (4 beats in 4/4)
    });
  }
  
  // Suggested tempo from genre
  const tempo = Math.floor(
    genreConfig.tempo.min + Math.random() * (genreConfig.tempo.max - genreConfig.tempo.min)
  );
  
  return {
    key,
    scale: isMinor ? 'minor' : scale,
    genre,
    chords,
    tempo,
    pattern: pattern.map(d => d + 1), // 1-indexed for display
    romanNumerals: pattern.map(d => getRomanNumeral(d, isMinor)),
  };
};

/**
 * Get chord symbol string (e.g., "Cmaj7", "Am", "Dm7")
 */
export const getChordSymbol = (root, chordType) => {
  const suffixes = {
    major: '',
    minor: 'm',
    diminished: 'dim',
    augmented: 'aug',
    major7: 'maj7',
    minor7: 'm7',
    dominant7: '7',
    diminished7: 'dim7',
    halfDiminished7: 'ø7',
    sus2: 'sus2',
    sus4: 'sus4',
    add9: 'add9',
    minorAdd9: 'm(add9)',
    major9: 'maj9',
    minor9: 'm9',
    power: '5',
  };
  
  return `${root}${suffixes[chordType] || ''}`;
};

/**
 * Get Roman numeral for scale degree
 */
export const getRomanNumeral = (degree, isMinor = false) => {
  const majorNumerals = ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'];
  const minorNumerals = ['i', 'ii°', 'III', 'iv', 'v', 'VI', 'VII'];
  
  const numerals = isMinor ? minorNumerals : majorNumerals;
  return numerals[degree % 7] || degree.toString();
};

/**
 * Convert chord progression to MIDI events for playback/export
 */
export const progressionToMidiEvents = (progression, bpm = 120) => {
  const events = [];
  const beatsPerBar = 4;
  const secondsPerBeat = 60 / bpm;
  
  progression.chords.forEach((chord, chordIndex) => {
    const startTime = chord.startBeat * secondsPerBeat;
    const duration = beatsPerBar * secondsPerBeat; // 1 bar duration
    
    chord.notes.forEach(note => {
      events.push({
        type: 'noteOn',
        note,
        velocity: 80 + Math.floor(Math.random() * 20), // Slight velocity variation
        time: startTime,
        chordIndex,
      });
      
      events.push({
        type: 'noteOff',
        note,
        time: startTime + duration - 0.05, // Small gap before next chord
        chordIndex,
      });
    });
  });
  
  return events.sort((a, b) => a.time - b.time);
};

/**
 * Generate random variation of a progression
 */
export const generateVariation = (progression) => {
  const varied = { ...progression, chords: [...progression.chords] };
  
  // Randomly modify one chord
  const indexToModify = Math.floor(Math.random() * varied.chords.length);
  const chord = varied.chords[indexToModify];
  
  // Options: change inversion, add extension, or substitute
  const variation = Math.floor(Math.random() * 3);
  
  if (variation === 0) {
    // Change inversion
    chord.notes = buildChord(chord.root, chord.type, 4, Math.floor(Math.random() * 3));
  } else if (variation === 1) {
    // Try to add extension if not already extended
    if (!chord.type.includes('7') && !chord.type.includes('9')) {
      const extendedType = chord.type === 'major' ? 'major7' : 
                          chord.type === 'minor' ? 'minor7' : chord.type;
      chord.type = extendedType;
      chord.notes = buildChord(chord.root, extendedType, 4, 0);
      chord.symbol = getChordSymbol(chord.root, extendedType);
    }
  }
  
  return varied;
};

export default {
  NOTES,
  SCALES,
  CHORD_TYPES,
  GENRE_PROGRESSIONS,
  generateChordProgression,
  generateVariation,
  progressionToMidiEvents,
  getChordSymbol,
  getRomanNumeral,
  buildChord,
  noteToMidi,
  midiToNote,
};
